import React, { useState } from 'react';
import eve from '../assets/eve.png';
import bob from '../assets/bob.png';
import alice from '../assets/alice.png';

const ElGamal = () => {
  const [prime, setPrime] = useState('');
  const [generator, setGenerator] = useState('');
  const [privateKey, setPrivateKey] = useState('');
  const [encryptedValue, setEncryptedValue] = useState(null);
  const [publicKey, setPublicKey] = useState(null);
  const [message, setMessage] = useState('');
  const [randomK, setRandomK] = useState(null);
  const [encryptedY1, setEncryptedY1] = useState(null);
  const [encryptedY2, setEncryptedY2] = useState(null);
  const [decryptedMessage, setDecryptedMessage] = useState(null);

  const isPrime = (num) => {
    for (let i = 2; i <= Math.sqrt(num); i++) {
      if (num % i === 0) {
        return false;
      }
    }
    return num > 1;
  };

  const gcd = (a, b) => (b === 0 ? a : gcd(b, a % b));

  const generateGOptions = () => {
    const primeNumber = parseInt(prime, 10);
    const options = [];

    for (let i = 2; i < primeNumber; i++) {
      if (gcd(i, primeNumber) === 1) {
        let isPrimitiveRoot = true;
        for (let j = 1; j < primeNumber - 1; j++) {
          if (Math.pow(i, j) % primeNumber === 1) {
            isPrimitiveRoot = false;
            break;
          }
        }

        if (isPrimitiveRoot) {
          options.push(i);
        }
      }
    }

    return options;
  };

  const calculateEncryptedValue = () => {
    const g = parseInt(generator, 10);
    const d = parseInt(privateKey, 10);
    const p = parseInt(prime, 10);

    return Math.pow(g, d) % p;
  };

  const calculatePublicKey = () => {
    const g = parseInt(generator, 10);
    const d = parseInt(privateKey, 10);
    const p = parseInt(prime, 10);

    return Math.pow(g, d) % p;
  };

  const generateRandomK = () => {
    // Generate a random k between 0 (inclusive) and p-1 (exclusive)
    const p = parseInt(prime, 10);
    return Math.floor(Math.random() * p);
  };

  const handleGenerateKeys = () => {
    // Add validation and error handling if needed
    const parsedPrime = parseInt(prime, 10);
    const parsedPrivateKey = parseInt(privateKey, 10);

    if (!isPrime(parsedPrime)) {
      alert('Please enter a prime number for p.');
      return;
    }

    if (parsedPrivateKey < 2 || parsedPrivateKey > parsedPrime - 2) {
      alert('Private key (d) must satisfy 2 <= d <= p-2.');
      return;
    }

    const selectedG = parseInt(generator, 10);
    const gOptions = generateGOptions();

    if (!gOptions.includes(selectedG)) {
      alert('Please select a valid primitive root (g).');
      return;
    }

    // Calculate encrypted value (e = g^d mod p)
    const e = calculateEncryptedValue();

    // Calculate public key
    const publicKeyValue = calculatePublicKey();

    // Generate a random k between 0 and p-1
    const randomKValue = generateRandomK();

    // Set the values in state for display
    setEncryptedValue(e);
    setPublicKey({ p: prime, g: generator, e });
    setRandomK(randomKValue);

    // Perform key generation logic here
    console.log('Generated keys:', { prime, generator, privateKey, e, publicKey: { p: prime, g: generator, e }, randomK: randomKValue });
  };

  const handleEncryptMessage = () => {
    if (message.trim() === '' || isNaN(message)) {
      alert('Please enter a valid numeric message (M).');
      return;
    }

    const m = parseInt(message, 10);

    // Ensure m is less than p
    const pValue = parseInt(prime, 10);
    if (m >= pValue) {
      alert('Message (m) must be less than p.');
      return;
    }

    // Ensure keys are generated first
    if (encryptedValue === null || publicKey === null || randomK === null) {
      alert('Please generate keys first.');
      return;
    }

    const g = parseInt(generator, 10);
    const k = randomK;
    const p = pValue;

    // Calculate Y1 = g^k mod p
    const y1 = Math.pow(g, k) % p;

    // Calculate Y2 = M * e^k mod p
    const y2 = (m * Math.pow(encryptedValue, k)) % p;

    // Set the encrypted values in state for display
    setEncryptedY1(y1);
    setEncryptedY2(y2);

    // Perform encryption logic here
    console.log('Encrypted values:', { y1, y2 });
  };

  const handleDecryptMessage = () => {
    if (encryptedY1 === null || encryptedY2 === null || privateKey === '') {
      alert('Cannot decrypt without necessary values.');
      return;
    }

    const d = parseInt(privateKey, 10);
    const p = parseInt(prime, 10);

    // Calculate the inverse of Y1 raised to the power of private key (d) mod p
    const y1Inverse = Math.pow(encryptedY1, p - 1 - d) % p;

    // Calculate the decrypted message M = Y2 * Y1^(-d) mod p
    const decryptedM = (encryptedY2 * y1Inverse) % p;

    // Set the decrypted message in state for display
    setDecryptedMessage(decryptedM);

    // Perform decryption logic here
    console.log('Decrypted Message:', decryptedM);
  };

  return (
    <div className="flex h-screen">
      {/* Agent 1's Private Space */}
      <div className="flex-1 bg-blue-200 p-4 h-full">
        <h2 className="text-xl font-bold mb-4">Alice (Private Space)</h2>
        <img src={alice} alt="Alice" className=" w-16 mx-auto " />
        While ElGamal supports variable key sizes, it is recommended to use a key size of at least 2048 bits for strong security in practical applications.
        <label className="block mb-2">
          Prime (p):
          <input
            className="border rounded px-2 py-1 w-full"
            type="number"
            value={prime}
            onChange={(e) => setPrime(e.target.value)}
          />
        </label>
        <label className="block mb-2">
          Generator (g):
          <select
            className="border rounded px-2 py-1 w-full"
            value={generator}
            onChange={(e) => setGenerator(e.target.value)}
          >
            <option value="">Select g</option>
            {generateGOptions().map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>
        <label className="block mb-2">
          Private Key (d):
          <input
            className="border rounded px-2 py-1 w-full focus:outline-none focus:ring-2 focus:ring-red-500"
            type="number"
            value={privateKey}
            onChange={(e) => setPrivateKey(e.target.value)}
          />
        </label>
        <button className="bg-blue-500 text-white px-4 py-2 rounded" onClick={handleGenerateKeys}>
          1. Generate Keys
        </button>
        {encryptedValue !== null && (
          <div className="mt-4">
            <strong>Encrypted Value (e):</strong> {encryptedValue}
          </div>
        )}
        
        {encryptedValue !== null && publicKey !== null && encryptedY1 !== null && encryptedY2 !== null && (
          <button className="bg-red-500 text-white px-4 py-2 rounded mt-4" onClick={handleDecryptMessage}>
            3. Decrypt Message from Bob
          </button>
        )}
        {decryptedMessage !== null && (
          <div className="mt-4">
            <strong>Decrypted Message (M):</strong> {message}
            <br />
            <br />
            <strong>Plaintext = Y2 * (Y1<sup>d</sup>)<sup>-1</sup> mod p</strong>
            <br />
            Plaintext = {encryptedY2} * ({encryptedY1}<sup>{privateKey}</sup>)<sup>-1</sup> mod {publicKey.p}
          </div>
        )}
      </div>

      {/* Agent 2's Public Space */}
      <div className="flex-1 bg-green-200 p-4 h-full">
        
        <h2 className="text-xl font-bold mb-4">Eve (Public Space)</h2>
        <img src={eve} alt="Eve" className=" w-20 mx-auto" />
        {publicKey !== null && (
          <div>
          <strong>Public Key:
            <br/>Prime (p):</strong> {publicKey.p}
          <br />
          <strong>Generator (g):</strong> {publicKey.g}
          <br />
          <strong>Public Key (e):</strong> {publicKey.e}
        </div>
        )}
        {/* Display other relevant information */}
        {encryptedY1 !== null && (
          <div className="mt-4">
            <strong>Encrypted Y1:</strong> {encryptedY1}
          </div>
        )}
        {encryptedY2 !== null && (
          <div className="mt-4">
            <strong>Encrypted Y2:</strong> {encryptedY2}
          </div>
        )}
      </div>

      {/* Agent 3's Private Space */}
      <div className="flex-1 bg-yellow-200 p-4 h-full">
        <h2 className="text-xl font-bold mb-4">Bob (Private Space)</h2>
        <img src={bob} alt="Bob" className="w-32 mx-auto" />
        <label className="block mb-2">
          Message (m):
          <input
            className="border rounded px-2 py-1 w-full"
            type="number"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
        </label>
        { publicKey !== '' && publicKey !== null && (
        <button className="bg-yellow-500 text-white px-4 py-2 rounded" onClick={handleEncryptMessage}>
          2. Encrypt Message
        </button>
        )}
        {/* Display other relevant information */}
        {randomK !== null && (
          <div className="mt-4">
            <strong>Selected random k:</strong> {randomK}
          </div>
        )}
        {encryptedY1 !== null && (
          <div className="mt-4">
            <strong>Encrypted Y1:</strong> {encryptedY1}
          </div>
        )}
        {encryptedY2 !== null && (
          <div className="mt-4">
            <strong>Encrypted Y2:</strong> {encryptedY2}
            <br />
            <br />
            <div className=' bg-yellow-100 w-48 rounded-md p-3'>
            <strong>
              Y1 = g<sup>k</sup> mod p
            </strong>
            <br/>
            Y1 = {publicKey.g}<sup>{randomK}</sup> mod {publicKey.p}
            <br />
            <br />
            <strong>
              Y2 = M * e<sup>k</sup> mod p
            </strong>
            <br />
            Y2 = {message} * {publicKey.e}<sup>{randomK}</sup> mod {publicKey.p}
          </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ElGamal;
